// What every chat screen needs about the company and the viewer (M2-06): the API, the caller's
// membership, the add-on states (chat itself; `agents` for cards, cost and status lines), the
// people and digital employees by membership (names, badges, the @ menu), and who is here now
// (presence, RT-01). People and DEs come from the same cached queries as Setup and Agents, so
// a chat screen adds no call for them once either has been opened.
import type { MentionItem } from "@companyos/ui";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import type { Api } from "../../api/client";
import { activity } from "../../live/activity";
import type { CompanyAccess } from "../company";
import { notificationKeys } from "../notifications/notification-queries";
import { digitalEmployeesQuery, peopleQuery } from "../queries";
import { type EveryPage, useEveryPage } from "../use-every-page";
import { useCompany, useFeature, useSession } from "../use-feature";
import { type ChatMember, type MemberIndex, roleLabelOf } from "./chat-model";
import {
  type AgentReadiness,
  chatKeys,
  type Peer,
  presenceQuery,
  threadAgentsQuery,
} from "./chat-queries";
import { type StatusIndex, useStatuses } from "./member-status";

export type ChatContext = {
  api: Api;
  company: CompanyAccess;
  co: string;
  /** The caller's membership here; agency operators read without one. */
  meId: string | null;
  meName: string;
  /** Chat is in its grace period: read, do not write. */
  readOnly: boolean;
  /** The `agents` add-on: approval cards, cost chips, run status. */
  agents: boolean;
  /** CHAT-COST-PILLS: the person's "Show agent costs in chat" setting (absent: on). */
  showCosts?: boolean;
  members: MemberIndex;
  mentionables: MentionItem[];
  /**
   * WF-FIX (2026-10-26): whether every page of the DEs (people) is in (`complete`), or a page
   * failed (`failed`, `retry`): the "Add digital employee" picker waits for the DEs'.
   */
  deDirectory: EveryPage;
  peopleDirectory: EveryPage;
  /** CHAT-SWITCH-A: people's presence and custom status by membership (the authors' emoji). */
  statuses?: StatusIndex;
};

export function useChat(): ChatContext {
  const { api } = useRouteContext({ from: "__root__" });
  const company = useCompany();
  const { me } = useSession();
  const chat = useFeature("chat");
  const agents = useFeature("agents");
  const co = company.id;
  const meId = me.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const replyStyle = useQuery({
    queryKey: notificationKeys.replyStyle(co),
    queryFn: () => api.client.replyStyle.get({ params: { co } }),
    enabled: agents.enabled,
  });
  const people = useInfiniteQuery(peopleQuery(api, co));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  // WF-FIX (2026-10-26): every page, not the first: a DE or person past it was missing from the
  // @ menu, the names and the "Add digital employee" pickers.
  const allPeople = useEveryPage(people);
  const allDes = useEveryPage(des);
  const peopleRows = people.data?.pages;
  const deRows = des.data?.pages;
  const members = useMemo(() => {
    const index = new Map<string, ChatMember>();
    for (const p of peopleRows?.flatMap((x) => x.data) ?? [])
      index.set(p.id, {
        id: p.id,
        name: p.display_name,
        kind: p.kind === "digital_employee" ? "de" : "person",
        role: roleLabelOf(p.roles ?? []),
        inactive: p.status !== "active",
      });
    for (const d of deRows?.flatMap((x) => x.data) ?? [])
      index.set(d.membership_id, {
        id: d.membership_id,
        name: d.display_name,
        kind: "de",
        seat: d.seat?.title,
        seatId: d.seat?.id,
        personName: d.person_name,
        deStatus: d.status,
      });
    if (meId && !index.has(meId)) index.set(meId, { id: meId, name: me.user.name, kind: "person" });
    return index as MemberIndex;
  }, [peopleRows, deRows, meId, me.user.name]);
  const mentionables = useMemo(() => mentionablesOf(members, meId), [members, meId]);
  const statuses = useStatuses(api, co, meId !== null && chat.enabled);
  return {
    api,
    company,
    co,
    meId,
    meName: me.user.name,
    readOnly: chat.readOnly,
    agents: agents.enabled,
    showCosts: replyStyle.data?.show_costs ?? true,
    members,
    mentionables,
    deDirectory: allDes,
    peopleDirectory: allPeople,
    statuses,
  };
}

/** The @ menu's entries: people, then every DE (WF-FIX: under "Digital employees"); not me. */
export function mentionablesOf(members: MemberIndex, meId: string | null): MentionItem[] {
  return [...members.values()]
    .filter((m) => m.id !== meId)
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "de" ? 1 : -1))
    .map(
      (m): MentionItem => ({
        id: `${m.kind === "de" ? "de" : "person"}:${m.id}`,
        name: m.name,
        kind: m.kind === "de" ? "agent" : "person",
        // WF-FIX (2026-10-26): DEs under their own heading, after the people.
        ...(m.kind === "de" ? { group: "Digital employees" } : {}),
        // A seat titled "… (DE)" (the demo's) would say DE twice: the hint leads with it.
        hint:
          m.kind === "de"
            ? `DE${m.seat ? ` · ${m.seat.replace(/\s*\(DE\)$/, "")}` : ""}`
            : "Person",
      }),
    );
}

/**
 * A presence socket of the company: says where the viewer is (`here`, when given), active /
 * away from the page's one activity tracker (CHAT-SWITCH-A) on open and on change, and passes on
 * the peer lists it is sent. Returns the closer (none when sockets are unavailable).
 */
export function openPresence(
  baseUrl: string,
  co: string,
  here: { screen: string; subject: { kind: string; id: string } | null } | null,
  onPeers?: (peers: Peer[]) => void,
): (() => void) | undefined {
  if (typeof WebSocket === "undefined") return;
  const origin = new URL(baseUrl, window.location.href);
  origin.protocol = origin.protocol === "https:" ? "wss:" : "ws:";
  origin.pathname = `${origin.pathname.replace(/\/$/, "")}/v1/companies/${encodeURIComponent(co)}/presence`;
  const tracker = activity();
  let socket: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  const sendActive = (active: boolean) => {
    if (socket?.readyState === WebSocket.OPEN)
      socket.send(JSON.stringify({ type: "active", active }));
  };
  // A dropped socket (network, an API restart) comes back with a bounded backoff (1 s … 30 s),
  // and says where the viewer is and whether they are active again.
  const connect = () => {
    if (closed) return;
    let s: WebSocket;
    try {
      s = new WebSocket(origin.toString());
    } catch {
      return;
    }
    socket = s;
    s.addEventListener("open", () => {
      attempt = 0;
      if (here) s.send(JSON.stringify({ type: "here", ...here }));
      sendActive(tracker.isActive());
    });
    if (onPeers)
      s.addEventListener("message", (e) => {
        try {
          const msg = JSON.parse(String(e.data)) as { type?: string; peers?: Peer[] };
          if (msg.type === "peers" && Array.isArray(msg.peers)) onPeers(msg.peers);
        } catch {}
      });
    s.addEventListener("close", () => {
      if (closed || socket !== s) return;
      const wait = Math.min(30_000, 1_000 * 2 ** attempt);
      attempt += 1;
      retry = setTimeout(connect, wait);
    });
  };
  const offActivity = tracker.subscribe(sendActive);
  connect();
  const ping = setInterval(() => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "ping" }));
  }, 25_000);
  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    clearInterval(ping);
    offActivity();
    socket?.close();
  };
}

/**
 * CHAT-SWITCH-A: the company shell's presence socket, open in every mode (not only Chat), so a
 * person using Work or KPIs reads as active and `@here` reaches them. `enabled`: chat is on.
 */
export function useCompanyActivity(api: Api, co: string, enabled: boolean): void {
  const baseUrl = api.mode === "http" ? api.baseUrl : undefined;
  useEffect(() => {
    if (!enabled || !co || baseUrl === undefined) return;
    return openPresence(baseUrl, co, null);
  }, [enabled, co, baseUrl]);
}

/**
 * Who is here (RT-01 presence): the snapshot route, refreshed every 30 s; against the real API
 * the presence socket also says where the viewer is and pushes the peer list as it changes.
 */
export function usePresence(
  api: Api,
  co: string,
  here?: { kind: string; id: string } | null,
): ReadonlySet<string> {
  const snapshot = useQuery(presenceQuery(api, co));
  const queryClient = useQueryClient();
  const baseUrl = api.mode === "http" ? api.baseUrl : undefined;
  const hereKind = here?.kind;
  const hereId = here?.id;
  useEffect(() => {
    if (baseUrl === undefined) return;
    const subject = hereKind && hereId ? { kind: hereKind, id: hereId } : null;
    return openPresence(baseUrl, co, { screen: "chat", subject }, (peers) =>
      queryClient.setQueryData(chatKeys.presence(co), { peers }),
    );
  }, [baseUrl, co, hereKind, hereId, queryClient]);
  return useMemo(
    () =>
      new Set(
        (snapshot.data?.peers ?? []).flatMap((p) => (p.membership_id ? [p.membership_id] : [])),
      ),
    [snapshot.data],
  );
}

/** AGENT-MENTION: the @ menu's status of an agent in this thread ("Not in #ops", "Paused"). */
export function readinessBadge(r: AgentReadiness, channelName: string | null): string {
  switch (r.state) {
    case "ready":
      return "Ready";
    case "no_access":
      return channelName ? `Not in #${channelName}` : "Can't see this";
    case "paused":
      return "Paused";
    case "silent":
      return "Silent channel";
    // REACH-S1: it does not take asks from the viewer (outside its Hears from).
    case "not_in_reach":
      return "Needs access";
    default:
      return "Not live yet";
  }
}

/**
 * AGENT-MENTION: the @ menu of a thread, each agent with whether a mention here would wake it
 * (the readiness route, one call per thread, loaded only when there are agents to mention).
 */
export function useThreadMentionables(chat: ChatContext, threadId: string | null): MentionItem[] {
  const hasAgents = chat.mentionables.some((m) => m.kind === "agent");
  const agents = useQuery({
    ...threadAgentsQuery(chat.api, chat.co, threadId ?? ""),
    enabled: hasAgents && threadId !== null && chat.meId !== null,
  });
  const data = agents.data;
  return useMemo(() => {
    if (!data) return chat.mentionables;
    const by = new Map(data.data.map((r) => [r.membership_id, r]));
    return chat.mentionables.map((m) => {
      const r = m.kind === "agent" ? by.get(m.id.slice(m.id.indexOf(":") + 1)) : undefined;
      return r ? { ...m, badge: readinessBadge(r, data.channel_name) } : m;
    });
  }, [chat.mentionables, data]);
}
